import { FC } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

export interface DealGuardrailSectionProps extends SectionBaseProps {
    /** Current deal guardrail value (% of original deal price) */
    dealPercentOfOriginalDealPrice: string;
    /** Callback to update the deal guardrail value */
    setDealPercentOfOriginalDealPrice: (value: string) => void;
    /** Inline validation error message */
    errorText?: string;
}

/**
 * Deal guardrail section — renders a single numeric input
 * for the percent of original deal price guardrail.
 * Only shown for DEAL campaign type (parent controls visibility).
 * Owns its own i18n bundle (`organisms.dealGuardrailSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const DealGuardrailSection: FC<DealGuardrailSectionProps> = ({
    dealPercentOfOriginalDealPrice,
    setDealPercentOfOriginalDealPrice,
    mode,
    disabled,
    errorText,
}) => {
    const { translate } = useTranslate('organisms.dealGuardrailSection');
    // Deal guardrail is read-only after creation — disabled in both view and modify modes.
    const isDisabled = disabled ?? mode !== 'create';

    return (
        <Container
            header={
                <Header variant="h2">{translate('section_deal_guardrails')}</Header>
            }
        >
            <FormField
                label={translate('deal_guardrail_label')}
                description={translate('deal_percent_of_original_deal_price_label')}
                errorText={errorText}
                i18nStrings={{ errorIconAriaLabel: translate('error_icon_aria_label') }}
            >
                <Input
                    value={dealPercentOfOriginalDealPrice}
                    onChange={({ detail }) => setDealPercentOfOriginalDealPrice(detail.value)}
                    type="number"
                    ariaRequired={true}
                    disabled={isDisabled}
                    data-testid="deal-percent-of-original-deal-price-guardrail"
                    invalid={!!errorText}
                />
            </FormField>
        </Container>
    );
};

export default DealGuardrailSection;
